import { useEffect, useState, useMemo, useRef } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { max, min } from 'd3-array'
import { scaleTime } from '@visx/scale'
import { withParentSize } from '@visx/responsive'
import LineGraphChart from './LineGraphChart'
import GraphLegend from './GraphLegend'
import LineGraphBrush from './LineGraphBrush'
import GraphButton from '../ui/Button'
import GraphBrushButtons from './GraphBrushButtons'
import { getValue, bisectDateLeft, bisectDateRight, getLayersExtents, getDate } from './graphUtils'
import { isFinancial } from '../../utils/tools'
import styles from './Graph.module.css'

const padding = 24
const graphHeight = 300
const brushHeight = 60
const graphMargin = { right: padding, left: padding, top: 0, bottom: 0 }

function LineGraph({
    layers,
    brush = true,
    legend = true,
    parentWidth,
    initialRange = [null, new Date()],
    graphId,
}) {
    const brushRef = useRef(null)
    const [brushRange, setBrushRange] = useState(initialRange)
    const [localLayers, setLocalLayers] = useState([])

    const { colors } = usePaletteTheme()

    const altYActive = useMemo(() => localLayers.reduce((acc, cur) => !!cur.yDomain || acc, false),[localLayers])

    const filterDateRange = (data, range) => {
        // filtering date range
        // adding 'fringes' to display a continous line (one point after and one before)
        let left = bisectDateLeft(data,range[0])
        let right = bisectDateRight(data, range[1])
        if (right === 0 || left === data.length) return [] // avoid adding 'fringes' to out of range layers
        left = left > 0 ? left - 1 : 0
        right = right + 1
        return data.slice(left, right)
    }

    const getAltDataExtents = (layers) => {
        return getLayersExtents(layers.filter(layer => !isFinancial(layer.dataset) && layer.display !== 'none'), getValue)
    }

    const getfilteredData = (layers) => {
        return layers.map(layer => ({...layer, data: brushRange.includes(null) ? layer.data : filterDateRange(layer.data, brushRange)}))
    }
    
    // filtered layers by date of brush range
    const filteredLayers = useMemo(() => {
        const filteredData =  getfilteredData(localLayers)
        const altDataExtents = getAltDataExtents(filteredData)
        return filteredData.map(layer => ({...layer, originalYDomain: altYActive && !isFinancial(layer.dataset) ? altDataExtents : [min(layer.data, getValue), max(layer.data, getValue)]}))
    }, [localLayers, brushRange, altYActive]) // eslint-disable-line
    
    // change layer display on legend click
    const handleLegendClick = (i) => {
        const modifiedSettings = [...localLayers]
        const { display, dataset } = modifiedSettings[i]
        let newMode = 'none'
        if (display === 'none') { newMode = isFinancial(dataset) ? 'area' : 'line'}
        modifiedSettings[i] = {...modifiedSettings[i], display: newMode }
        setLocalLayers(altYActive ? updateYScale(modifiedSettings) : modifiedSettings)
    }

    // makes all layers have the same y scale - based on the largest value
    const handleYScaleButtonClick = () => setLocalLayers(altYActive ? removeYScale : updateYScale)

    const updateYScale = (layersState) => {
        const altDataExtents = getAltDataExtents(layersState)
        return layersState.map((layer) => ({ ...layer, yDomain: isFinancial(layer.dataset) ? undefined : altDataExtents }))
    }
    const removeYScale = (layersState) => layersState.map(layer => ({...layer, yDomain: undefined}))

    // update local layers on prop change
    useEffect(() => {
        setLocalLayers(layers)
        return () => {}
    }, [layers]) // eslint-disable-line

    // calculates maximum x scale for brush (and brush buttons)
    const maxXScale = useMemo(() => {
        const xMax = Math.max(parentWidth - graphMargin.left - graphMargin.right, 0)
        const extents = [ getLayersExtents(localLayers, getDate)[0], new Date() ]
        return scaleTime({
          range: [0, xMax],
          domain: extents
        })
    }, [localLayers, parentWidth])

    return (
        <div className={`${styles['relative']} ${styles['v-stack']}`} style={{ paddingTop: padding, paddingBottom: padding }}>
             <div className={styles['h-stack']} style={{ marginRight: graphMargin.right, marginLeft: graphMargin.left }}>
                { brush && (
                    <GraphBrushButtons brushRef={brushRef} margin={graphMargin} setBrushRange={setBrushRange} xScale={maxXScale} resetRange={initialRange} brushRange={brushRange} activeColor={colors.textPrimary}/>
                )}
                <GraphButton outlinecolor={colors.textPrimary} $active={altYActive} onClick={handleYScaleButtonClick}>Scale Y</GraphButton>
            </div>
           
            <div id={graphId}>
                { legend && (
                    <GraphLegend
                        margin={graphMargin}
                        layers={localLayers}
                        handleClick={handleLegendClick}
                    />
                )}
                { localLayers.length > 0 && (
                    <div className={styles.relative}>
                        <LineGraphChart
                            height={ graphHeight }
                            margin={ graphMargin }
                            layers={ filteredLayers }
                            domain={ brushRange }
                        />
                    </div>
                )}
            </div>
            { localLayers.length > 0 && brush && (
                <LineGraphBrush
                    height={ brushHeight }
                    margin={ graphMargin }
                    layers={ localLayers }
                    setBrushRange={ setBrushRange }
                    resetRange={ initialRange }
                    brushRef={ brushRef }
                    xScale={ maxXScale }
                />
            )}
        </div>
    )
}

export default withParentSize(LineGraph)